import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { Loader2, Plus, Trash2 } from 'lucide-react';
import {
    getCCResource, getCCResourceVerify, saveResourceHead, saveResourceVHead, deleteResource,
} from '../../api/AccountsAPI/ccClosingAPI';

/**
 * "Resource consumed summary in project" grid for CC Closing (closing type 5).
 * Legacy: Reports/ViewCCCloseResourceConsumedSummary (create — master heads, add/delete heads)
 *         Reports/ViewCCCloseResourceConsumedVerifySummary (verify — the CC's stored heads + details, add heads).
 * `rows` = [{ head, detail }]; the parent builds the Heads / HeadDetails CSVs (trailing commas) from it.
 */
const dataOf = (res) => res?.data?.Data ?? res?.data ?? [];

const ResourceConsumedGrid = ({ mode = 'create', ccCode = '', rows, onChange, disabled = false }) => {
    const [loading, setLoading] = useState(false);
    const [newHead, setNewHead] = useState('');
    const [busy, setBusy] = useState(false);

    const load = async () => {
        if (mode === 'verify' && !ccCode) return;
        setLoading(true);
        try {
            const res = mode === 'verify' ? await getCCResourceVerify(ccCode) : await getCCResource();
            const list = Array.isArray(dataOf(res)) ? dataOf(res) : [];
            // keep any detail already typed for a head that is still in the list
            const typed = Object.fromEntries((rows || []).map((r) => [r.head, r.detail]));
            onChange(list.map((r) => {
                const head = String(r.Resources || '').trim();
                return { head, detail: typed[head] ?? (mode === 'verify' ? (r.ResDetails || '') : '') };
            }));
        } catch {
            toast.error('Failed to load resource heads');
        } finally {
            setLoading(false);
        }
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => { load(); }, [mode, ccCode]);

    const handleAdd = async () => {
        const head = newHead.trim();
        if (!head) { toast.warn('Add Resource Head'); return; }
        setBusy(true);
        try {
            const res = mode === 'verify' ? await saveResourceVHead(head, ccCode) : await saveResourceHead(head);
            const status = String(dataOf(res) || '').trim();
            if (status !== 'Submitted') { toast.error(status || 'Failed to add resource head'); return; }
            setNewHead('');
            await load();
        } catch {
            toast.error('Failed to add resource head');
        } finally {
            setBusy(false);
        }
    };

    const handleDelete = async (head) => {
        setBusy(true);
        try {
            const res = await deleteResource(head);
            const status = String(dataOf(res) || '').trim();
            if (status !== 'Submitted') { toast.error(status || 'Failed to delete resource head'); return; }
            await load();
        } catch {
            toast.error('Failed to delete resource head');
        } finally {
            setBusy(false);
        }
    };

    const setDetail = (idx, value) => {
        onChange(rows.map((r, i) => (i === idx ? { ...r, detail: value } : r)));
    };

    return (
        <div className="space-y-3">
            <p className="text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wide">
                Resource consumed summary in project (updated by PM manually)
            </p>
            <div className="flex gap-2">
                <input
                    type="text"
                    value={newHead}
                    onChange={(e) => setNewHead(e.target.value)}
                    placeholder="New resource head"
                    disabled={disabled || busy}
                    className="flex-1 px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 text-sm bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100"
                />
                <button type="button" onClick={handleAdd} disabled={disabled || busy}
                    className="flex items-center gap-1 px-3 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold disabled:opacity-50">
                    {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Add
                </button>
            </div>
            <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
                <table className="w-full text-sm">
                    <thead className="bg-gray-50 dark:bg-gray-700/50">
                        <tr>
                            <th className="px-3 py-2 text-left text-xs font-bold text-gray-500 uppercase">Description</th>
                            <th className="px-3 py-2 text-left text-xs font-bold text-gray-500 uppercase">Details</th>
                            {mode === 'create' && <th className="px-3 py-2" />}
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr><td colSpan={3} className="px-3 py-4 text-center text-gray-400"><Loader2 className="w-4 h-4 animate-spin inline" /></td></tr>
                        ) : (rows || []).length === 0 ? (
                            <tr><td colSpan={3} className="px-3 py-4 text-center text-gray-400 text-xs">No resource heads</td></tr>
                        ) : rows.map((r, idx) => (
                            <tr key={r.head} className="border-t border-gray-100 dark:border-gray-700">
                                <td className="px-3 py-2 text-gray-800 dark:text-gray-100">{r.head}</td>
                                <td className="px-3 py-2">
                                    <input
                                        type="text"
                                        value={r.detail}
                                        onChange={(e) => setDetail(idx, e.target.value)}
                                        disabled={disabled}
                                        className="w-full px-2 py-1.5 rounded-md border border-gray-300 dark:border-gray-600 text-sm uppercase bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100"
                                    />
                                </td>
                                {mode === 'create' && (
                                    <td className="px-3 py-2 text-right">
                                        <button type="button" onClick={() => handleDelete(r.head)} disabled={disabled || busy}
                                            className="p-1.5 rounded-md text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-900/20 disabled:opacity-50" title="Delete">
                                            <Trash2 className="w-4 h-4" />
                                        </button>
                                    </td>
                                )}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

// Legacy CSV format: "a,b," — commas inside a detail are stripped (the SP splits on commas).
export const resourceCsv = (rows) => ({
    heads: (rows || []).map((r) => `${String(r.head).replace(/,/g, '')},`).join(''),
    details: (rows || []).map((r) => `${String(r.detail || '').trim().replace(/,/g, '')},`).join(''),
});

export default ResourceConsumedGrid;
